/**
 * The learner's IELTS side (D-105): the exams followed and their access, the goal / date / centre /
 * placement of each exam (academy/me `exam_profiles`), the format of the exam (academy/format), the
 * progress in bands (academy/progress?exam=ielts_general) and the « next step » of the home.
 */

import { type LocalizedText, localizedText } from "./course";
import { isObject, num, numOrNull, numbers, objects, str } from "./read";
import { type IeltsSkill, IELTS_SKILLS, isIeltsSkill } from "./sitting";

// --- Exams ----------------------------------------------------------------------------------------

export const TCF_EXAM = "tcf_canada";
export const IELTS_EXAM = "ielts_general";
/** TEF Canada (D-109): the third exam, French, NCLC goal like the TCF. */
export const TEF_CANADA_EXAM = "tef_canada";
/** German, Goethe-Zertifikat A1 to C2 (D-118): one exam with a level dimension. */
export const GOETHE_EXAM_KEY = "goethe";
export type ExamKey = typeof TCF_EXAM | typeof IELTS_EXAM | typeof TEF_CANADA_EXAM | typeof GOETHE_EXAM_KEY;
export const EXAM_KEYS: readonly ExamKey[] = [TCF_EXAM, IELTS_EXAM, TEF_CANADA_EXAM, GOETHE_EXAM_KEY];

export function isExamKey(value: unknown): value is ExamKey {
  return value === TCF_EXAM || value === IELTS_EXAM || value === TEF_CANADA_EXAM || value === GOETHE_EXAM_KEY;
}

/** Query parameters of a learner route: nothing for the TCF (the server's default), `exam` otherwise. */
export function examParams<E extends string>(exam: E): { exam: E } | undefined {
  return exam === TCF_EXAM ? undefined : { exam };
}

/** The exams the learner follows (the TCF when the server lists none). */
export function followedExams(learner: { exams?: unknown }): ExamKey[] {
  const list = (Array.isArray(learner.exams) ? learner.exams : []).filter(isExamKey);
  return list.length ? list : [TCF_EXAM];
}

/** Paid access for one exam: the TCF keeps `has_access`, the IELTS has its own plans (`access`). */
export function hasExamAccess(learner: { has_access?: unknown; access?: unknown }, exam: string): boolean {
  if (exam === TCF_EXAM) return learner.has_access === true;
  return isObject(learner.access) && learner.access[exam] === true;
}

/** D-160: the only exams the student space shows (academy/me `space_exams`, the exam chosen at
 * sign-up first, then those bought or opened by the agency). Every exam when the server sends none
 * (a learner registered before D-160). */
export function spaceExams(learner: { space_exams?: unknown }): ExamKey[] {
  const list = (Array.isArray(learner.space_exams) ? learner.space_exams : []).filter(isExamKey);
  return list.length ? [...new Set(list)] : [...EXAM_KEYS];
}

/** D-120: the exams the switch offers, within the student space (D-160). A learner with an access
 * sees the exams opened to them (the agency may open some only: TEF and German, say); a learner
 * without any access sees every exam of their space (to discover and buy it). */
export function openExams(learner: { access?: unknown; space_exams?: unknown }): ExamKey[] {
  const access = isObject(learner.access) ? learner.access : {};
  const mine = spaceExams(learner);
  const open = mine.filter((exam) => access[exam] === true);
  return open.length ? open : mine;
}

// --- Sittings in progress (D-160) -----------------------------------------------------------------

export interface SittingRow {
  id: string;
  kind?: string;
  status?: string;
}

/** The unfinished sittings of one exam, kept apart by kind: a paused mock exam never stands for the
 * placement test (and the other way round); each one is resumed on its own. */
export function openSittings<T extends SittingRow>(rows: readonly T[] | null | undefined): { placement?: T; other?: T } {
  const open = (rows ?? []).filter((row) => row.status === "in_progress");
  return { placement: open.find((row) => row.kind === "placement"), other: open.find((row) => row.kind !== "placement") };
}

export type SittingStep<T> = { step: "placement"; open?: T } | { step: "resume"; open: T } | null;

/** The sitting the home suggests first, the same on the web and the phone: the placement test in
 * progress, else the placement test when the learner is not placed yet, else the other sitting in
 * progress (mock exam, practice…), else nothing. */
export function sittingStep<T extends SittingRow>(rows: readonly T[] | null | undefined, placed: boolean): SittingStep<T> {
  const { placement, other } = openSittings(rows);
  if (placement) return { step: "placement", open: placement };
  if (!placed) return { step: "placement" };
  if (other) return { step: "resume", open: other };
  return null;
}

// --- Profiles -------------------------------------------------------------------------------------

export interface ExamProfile {
  exam: string;
  target: number | null;
  examDate: string | null;
  examCenter: string;
  beginner: boolean;
  courseLevel: string;
  /** The placement test was taken (or the learner said « beginner »). */
  placed: boolean;
  placementBand: number | null;
  placementLevel: string;
  placementCefr: string;
  placementAt: string;
  xp: number;
  hasAccess: boolean;
}

export function parseProfiles(value: unknown): ExamProfile[] {
  return objects(value).map((row) => {
    const placement = isObject(row.placement) ? row.placement : {};
    const beginner = row.beginner === true;
    return {
      exam: str(row.exam),
      target: numOrNull(row.target),
      examDate: str(row.exam_date) || null,
      examCenter: str(row.exam_center),
      beginner,
      courseLevel: str(row.course_level),
      placed: Object.keys(placement).length > 0 || beginner,
      placementBand: numOrNull(placement.band),
      placementLevel: str(placement.level),
      placementCefr: str(placement.cefr),
      placementAt: str(placement.at),
      xp: num(row.xp),
      hasAccess: row.has_access === true,
    };
  });
}

/** The profile of one exam (academy/me `exam_profiles`), or null. */
export function examProfile(profiles: unknown, exam: string): ExamProfile | null {
  return parseProfiles(profiles).find((p) => p.exam === exam) ?? null;
}

// --- Format ---------------------------------------------------------------------------------------

export interface IeltsFormat {
  levels: { key: string; cefr: string; band: string; label: LocalizedText }[];
  rubrics: Record<string, { key: string; label: LocalizedText }[]>;
  /** `confirmed`: false for a centre the official sources did not confirm yet (TEF, D-109). */
  centers: { code: string; city: string; name: string; confirmed: boolean }[];
  listeningMinutes: number;
  readingMinutes: number;
  writingMinutes: number;
}

export function parseIeltsFormat(value: unknown): IeltsFormat {
  const data = isObject(value) ? value : {};
  const details = isObject(data.details) ? data.details : {};
  const course = isObject(details.course) ? details.course : {};
  const sections = isObject(data.sections) ? data.sections : {};
  const minutes = (skill: string) => {
    const row = sections[skill];
    return isObject(row) ? num(row.minutes) : 0;
  };
  const rubrics: IeltsFormat["rubrics"] = {};
  if (isObject(data.rubrics))
    for (const [skill, rows] of Object.entries(data.rubrics))
      if (Array.isArray(rows)) rubrics[skill] = objects(rows).map((r) => ({ key: str(r.key), label: localizedText(r.label) }));
  return {
    levels: objects(course.levels).map((l) => ({
      key: str(l.key),
      cefr: str(l.cefr),
      band: str(l.band),
      label: localizedText(l.label),
    })),
    rubrics,
    centers: objects(data.centers)
      .map((c) => ({ code: str(c.code), city: str(c.city), name: str(c.name), confirmed: c.confirmed !== false }))
      .filter((c) => c.city),
    listeningMinutes: minutes("co"),
    readingMinutes: minutes("ce"),
    writingMinutes: minutes("ee"),
  };
}

// --- Progress -------------------------------------------------------------------------------------

export interface IeltsSkillAnalysis {
  band: number | null;
  clb: number | null;
  gap: number | null;
  trend: "up" | "down" | "stable" | "unknown";
  history: number[];
}

export interface IeltsCriticalPoint {
  code: string;
  skill: IeltsSkill | null;
  message: string;
  gap: number | null;
  count: number | null;
  daysLeft: number | null;
  weeksNeeded: number | null;
}

export interface IeltsRank {
  position: number;
  total: number;
  topPercent: number;
}

export interface IeltsEvolutionRow {
  attempt: string;
  kind: string;
  mock: number | null;
  at: string;
  overall: number | null;
  skills: Partial<Record<IeltsSkill, { band: number | null; cefr: string }>>;
}

/** IELTS progress: bands per skill, overall band, the analysis, the evolution and the rank. */
export interface IeltsProgress {
  /** False while the server has no analysis yet (no sitting graded). */
  analysed: boolean;
  verdict: "ready" | "almost" | "not_ready";
  target: number;
  overall: number | null;
  skills: Record<IeltsSkill, IeltsSkillAnalysis>;
  critical: IeltsCriticalPoint[];
  daysLeft: number | null;
  evolution: IeltsEvolutionRow[];
  platform: IeltsRank | null;
  agency: IeltsRank | null;
  xp: number;
  streakDays: number;
}

function parseRank(value: unknown): IeltsRank | null {
  if (!isObject(value) || typeof value.position !== "number") return null;
  return { position: num(value.position), total: num(value.total), topPercent: num(value.top_percent, 100) };
}

/** The band aimed at when the learner set none (the analysis default of the server). */
export const DEFAULT_TARGET_BAND = 6.5;

export function parseIeltsProgress(value: unknown): IeltsProgress {
  const data = isObject(value) ? value : {};
  const analysis = isObject(data.analysis) ? data.analysis : {};
  const rawSkills = isObject(analysis.skills) ? analysis.skills : {};
  const skills = {} as Record<IeltsSkill, IeltsSkillAnalysis>;
  for (const skill of IELTS_SKILLS) {
    const raw = rawSkills[skill];
    const row = isObject(raw) ? raw : {};
    const trend = row.trend === "up" || row.trend === "down" || row.trend === "stable" ? row.trend : "unknown";
    skills[skill] = {
      band: numOrNull(row.band),
      clb: numOrNull(row.clb),
      gap: numOrNull(row.gap),
      trend,
      history: numbers(row.history),
    };
  }
  const rank = isObject(data.rank) ? data.rank : {};
  const verdict = analysis.verdict === "ready" || analysis.verdict === "almost" ? analysis.verdict : "not_ready";
  return {
    analysed: isObject(data.analysis),
    verdict,
    target: num(analysis.target, DEFAULT_TARGET_BAND),
    overall: numOrNull(analysis.overall),
    skills,
    critical: objects(analysis.critical).map((c) => ({
      code: str(c.code),
      skill: isIeltsSkill(c.skill) ? c.skill : null,
      message: str(c.message),
      gap: numOrNull(c.gap),
      count: numOrNull(c.count),
      daysLeft: numOrNull(c.days_left),
      weeksNeeded: numOrNull(c.weeks_needed),
    })),
    daysLeft: numOrNull(analysis.days_left),
    evolution: objects(data.evolution).map((row) => {
      const raw = isObject(row.skills) ? row.skills : {};
      const points: Partial<Record<IeltsSkill, { band: number | null; cefr: string }>> = {};
      for (const skill of IELTS_SKILLS) {
        const p = raw[skill];
        if (isObject(p)) points[skill] = { band: numOrNull(p.band) ?? numOrNull(p.score), cefr: str(p.cefr) };
      }
      return {
        attempt: str(row.attempt),
        kind: str(row.kind),
        mock: numOrNull(row.mock),
        at: str(row.at),
        overall: numOrNull(row.overall),
        skills: points,
      };
    }),
    platform: parseRank(rank.platform),
    agency: parseRank(rank.agency),
    xp: num(data.xp),
    streakDays: num(data.streak_days),
  };
}

// --- Next step ------------------------------------------------------------------------------------

export type IeltsNextStep = "resume" | "placement" | "plan" | "review" | "lesson" | "mock";

/**
 * The one clear next step of the IELTS home, the same on the web and the phone: the free placement
 * (resumed when it is open, D-160), resume the other sitting open (nothing is lost), choose a plan,
 * the review of the day (missed exercises come back before new ones), the next lesson, else a mock
 * exam. `openAttempt` is a sitting other than the placement test: a paused mock exam never hides
 * the placement test.
 */
export function ieltsNextStep(state: {
  openAttempt: boolean;
  openPlacement?: boolean;
  placed: boolean;
  hasAccess: boolean;
  reviewsDue: number;
  nextLesson: string | null | undefined;
}): IeltsNextStep {
  if (state.openPlacement || !state.placed) return "placement";
  if (state.openAttempt) return "resume";
  if (!state.hasAccess) return "plan";
  if (state.reviewsDue > 0) return "review";
  if (state.nextLesson) return "lesson";
  return "mock";
}
